import { t } from 'i18next';
import { Outlet, RouteObject } from 'react-router-dom';
import {
  _TimetableVersionsDocument,
  _TimetableVersionsQuery,
  _TimetableVersionsQueryVariables,
  TimetableVersionsQuery,
} from '../../../types/planung-graphql-client-defs';
import { addLeadingZero } from '../../../utils/helper';
import { filterLoader } from '../../../utils/routes-loader/filterLoader';
import { teachingBlockVersionLoader } from '../../../utils/routes-loader/teachingBlockVersionLoader';
import { urqlClient } from '../../../utils/urqlClient';
import { TeachingBlockVersionBoard } from './TeachingBlockVersion/Board/TeachingBlockVersionBoard';
import { PlanTeachingBlockClasses } from './TeachingBlockVersion/Data/Classes/PlanTeachingBlockClasses';
import { PlanTeachingBlockDependencies } from './TeachingBlockVersion/Data/Dependencies/PlanTeachingBlockDependencies';
import { PlanTeachingBlockLessons } from './TeachingBlockVersion/Data/Lessons/PlanTeachingBlockLessons';
import { PlanTeachingBlockRooms } from './TeachingBlockVersion/Data/Rooms/PlanTeachingBlockRooms';
import { PlanTeachingBlockSubjects } from './TeachingBlockVersion/Data/Subjects/PlanTeachingBlockSubjects';
import { PlanTeachingBlockTeachers } from './TeachingBlockVersion/Data/Teachers/PlanTeachingBlockTeachers';
import { TeachingBlockVersionData } from './TeachingBlockVersion/Data/TeachingBlockVersionData';
import { TeachingBlockVersion } from './TeachingBlockVersion/TeachingBlockVersion';
import { TimetablePlanPage } from './TimetablePlanPage';
import { Board } from './TimetableVersion/Board/Board';
import { PlanTimetableClasses } from './TimetableVersion/Data/Classes/PlanTimetableClasses';
import { PlanTimetableDependencies } from './TimetableVersion/Data/Dependencies/PlanTimetableDependencies';
import { PlanTimetableLessons } from './TimetableVersion/Data/Lessons/PlanTimetableLessons';
import { PlanTimetableRooms } from './TimetableVersion/Data/Rooms/PlanTimetableRooms';
import { PlanTimetableSubjects } from './TimetableVersion/Data/Subjects/PlanTimetableSubjects';
import { PlanTimetableTeachers } from './TimetableVersion/Data/Teachers/PlanTimetableTeachers';
import { TimetableVersionData } from './TimetableVersion/Data/TimetableVersionData';
import { TimetableVersion } from './TimetableVersion/TimetableVersion';

export const timetablePlanRoutes = (baseSlug: string): RouteObject[] => {
  return [
    {
      element: <TimetablePlanPage />,
      index: true,
    },
    // TIMETABLE
    {
      path: t('routes.versions.slug'),
      element: <Outlet />,
      children: [
        {
          path: ':versionUuid',
          id: 'version',
          element: <TimetableVersion baseSlug={baseSlug} />,
          loader: ({ params }) => {
            return urqlClient
              .query<_TimetableVersionsQuery, _TimetableVersionsQueryVariables>(_TimetableVersionsDocument, {
                where: {
                  uuid: params.versionUuid ?? '',
                },
              })
              .toPromise();
          },
          handle: {
            crumb: ({ data }: { data: TimetableVersionsQuery }) => {
              return [
                {
                  slug: `${baseSlug}/${t('routes.versions.slug')}/${data.timetableVersions[0].uuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
                  title: `${data.timetableVersions[0].timetable.name} - ${addLeadingZero(
                    data.timetableVersions[0].version,
                  )}`,
                },
              ];
            },
          },
          children: [
            {
              path: t('routes.versionPages.data.slug'),
              element: <TimetableVersionData />,
              children: [
                {
                  path: t('routes.versionsFilter.class.slug'),
                  element: <PlanTimetableClasses />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.class.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.subject.slug'),
                  element: <PlanTimetableSubjects />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.subject.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.person.slug'),
                  element: <PlanTimetableTeachers />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.person.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.room.slug'),
                  element: <PlanTimetableRooms />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.room.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.lesson.slug'),
                  element: <PlanTimetableLessons />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.lesson.title'),
                    }),
                  },
                  loader: filterLoader,
                },
                {
                  path: t('teachingBlockSubjectContainer.routes.timetableVersion.slug'),
                  element: <PlanTimetableLessons subjectContainerLessons={true} />,
                  handle: {
                    crumb: () => ({
                      title: t('teachingBlockSubjectContainer.routes.timetableVersion.title'),
                    }),
                  },
                  loader: filterLoader,
                },
                {
                  path: t('routes.versionsFilter.dependency.slug'),
                  element: <PlanTimetableDependencies />,
                  handle: {
                    crumb: () => ({
                      title: t('routes.versionsFilter.dependency.title'),
                    }),
                  },
                },
              ],
            },
            {
              path: t('routes.versionPages.board.slug'),
              element: <Board />,
              handle: {
                crumb: () => ({
                  title: t('routes.versionPages.board.title'),
                }),
              },
            },
          ],
        },
      ],
    },

    // TEACHINGBLOCK
    {
      path: t('routes.teachingBlockVersions.slug'),
      element: <Outlet />,
      children: [
        {
          path: ':teachingBlockVersionUuid',
          id: 'teachingBlockVersion',
          element: <TeachingBlockVersion baseSlug={baseSlug} />,
          loader: teachingBlockVersionLoader,
          handle: {
            crumb: (loaderResponse: { subjectContainerName: string; versionName: string; versionUuid: string }) => {
              return [
                {
                  slug: `${baseSlug}/${t('routes.teachingBlockVersions.slug')}/${loaderResponse.versionUuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
                  title: `${loaderResponse.subjectContainerName} - ${loaderResponse.versionName}`,
                },
              ];
            },
          },
          children: [
            {
              path: t('routes.versionPages.data.slug'),
              element: <TeachingBlockVersionData />,
              children: [
                {
                  path: t('routes.versionsFilter.class.slug'),
                  element: <PlanTeachingBlockClasses />,
                  handle: {
                    crumb: () => ({
                      slug: `${baseSlug}/${t('routes.versions.slug')}/${t('routes.versionsFilter.class.slug')}`,
                      title: t('routes.versionsFilter.class.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.subject.slug'),
                  element: <PlanTeachingBlockSubjects />,
                  handle: {
                    crumb: () => ({
                      slug: `${baseSlug}/${t('routes.versions.slug')}/${t('routes.versionsFilter.subject.slug')}`,
                      title: t('routes.versionsFilter.subject.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.person.slug'),
                  element: <PlanTeachingBlockTeachers />,
                  handle: {
                    crumb: () => ({
                      slug: `${baseSlug}/${t('routes.versions.slug')}/${t('routes.versionsFilter.person.slug')}`,
                      title: t('routes.versionsFilter.person.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.room.slug'),
                  element: <PlanTeachingBlockRooms />,
                  handle: {
                    crumb: () => ({
                      slug: `${baseSlug}/${t('routes.versions.slug')}/${t('routes.versionsFilter.room.slug')}`,
                      title: t('routes.versionsFilter.room.title'),
                    }),
                  },
                },
                {
                  path: t('routes.versionsFilter.lesson.slug'),
                  element: <PlanTeachingBlockLessons />,
                  handle: {
                    crumb: () => ({
                      slug: `${baseSlug}/${t('routes.versions.slug')}/${t('routes.versionsFilter.lesson.slug')}`,
                      title: t('routes.versionsFilter.lesson.title'),
                    }),
                  },
                  loader: filterLoader,
                },
                {
                  path: t('routes.versionsFilter.dependency.slug'),
                  element: <PlanTeachingBlockDependencies />,
                  handle: {
                    crumb: () => ({
                      slug: `${baseSlug}/${t('routes.versions.slug')}/${t('routes.versionsFilter.dependency.slug')}`,
                      title: t('routes.versionsFilter.dependency.title'),
                    }),
                  },
                },
              ],
              loader: ({ params }) => {
                return { activeTimetableVersionTab: 'data' };
              },
            },
            {
              path: t('routes.versionPages.board.slug'),
              element: <TeachingBlockVersionBoard />,
              loader: () => {
                return { activeTimetableVersionTab: 'board' };
              },
            },
          ],
        },
      ],
    },
  ];
};
